// src/screens/CustomerSampleScreen.js
// ✅ DROP-IN REPLACEMENT
// ✅ NEW STANDARD: sampleOrders identity fields:
//    - createdBy (uid)
//    - createdByName
//    - createdByRole
// ✅ Customer orders load query uses createdBy (prevents rules failures)
// ✅ Verification email uses continue URL + handleCodeInApp: true
// ✅ NO requestedBy / requestor / legacy

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  getAuth,
  onAuthStateChanged,
  signOut,
  sendEmailVerification,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  getDocs,
  addDoc,
  updateDoc,
  serverTimestamp,
  query,
  where,
  onSnapshot,
} from "firebase/firestore";

import { ClipboardList, List, BookOpen, Lock, Package } from "lucide-react";

import CustomerSampleForm from "../components/customer/CustomerSampleForm";
import CustomerSampleOrders from "../components/customer/CustomerSampleOrders";
import CustomerInfoPanel from "../components/customer/CustomerInfoPanel";
import CustomerCatalogWithCart from "../components/customer/CustomerCatalogWithCart";
import SurfaceState from "../components/common/SurfaceState";
import { SampleOrderCommentsService } from "../components/sampleorders/SampleOrderCommentsService";
import { findDuplicateSampleRequest } from "../utils/sampleOrderGuardrails";
import { shouldUseIndustrySampleStatusLabels } from "../utils/sampleOrderStatusLabels";
import {
  getCustomerMembershipAccountIds,
  hydrateCustomerProfileWithMemberships,
  loadCustomerMemberships,
  setStoredActiveCustomerCompany,
  upsertCustomerMembership,
} from "../auth/customerMemberships";

import useSampleOrderCart from "../hooks/useSampleOrderCart";

/**
 * Match App-style base path (supports /app hosting)
 */
const getAppBasePath = () => {
  let base = (process.env.PUBLIC_URL || "").trim();

  if (base === "/" || base === "") base = "";
  if (base) {
    base = base.replace(/\/+$/g, "");
    if (!base.startsWith("/")) base = `/${base}`;
  }

  if (!base) {
    const firstSeg = (window.location.pathname || "").split("/").filter(Boolean)[0];
    if (firstSeg === "app") base = "/app";
  }

  return base;
};

const getContinueUrlForEmailVerification = () => {
  const base = getAppBasePath();
  return `${window.location.origin}${base}/login?mode=verifyEmail`;
};

const FEEDBACK_INTEREST_LABELS = {
  hot: "Strong interest",
  warm: "Interested with tweaks",
  cold: "Not a fit right now",
};

const FEEDBACK_NEXT_STEP_LABELS = {
  follow_up: "Have my rep follow up",
  quote: "Send pricing / order info",
  resample: "Try a revised sample",
  no_action: "No action needed",
};

/**
 * Pick the customer's primary account from their visible accounts.
 */
function pickMyAccount({ accounts = [], userProfile }) {
  if (!accounts.length) return null;

  const primaryId =
    userProfile?.primaryAccountId ||
    userProfile?.primaryLocationId ||
    userProfile?.primaryCustomerAccountId ||
    userProfile?.accountId ||
    null;

  if (primaryId) {
    const byPrimary = accounts.find((a) => a.id === primaryId);
    if (byPrimary) return byPrimary;
  }

  // Default to first account
  return accounts[0] || null;
}

function formatFirestoreTimestamp(ts) {
  if (!ts) return "";
  if (ts?.seconds) return new Date(ts.seconds * 1000).toLocaleDateString();
  if (ts instanceof Date) return ts.toLocaleDateString();
  return "";
}

/**
 * ✅ ENHANCED: Customer account loader
 * Finds accounts by:
 * 1. Direct accountId link in user profile
 * 2. Matching primaryContact.email or contactEmail
 * 3. Accounts assigned to customer's rep (repUserId)
 */
async function loadCustomerAccounts(db, compId, userData, membership = null) {
  try {
    const byId = new Map();
    const customerUid = (userData?.uid || "").trim();

    const customerEmail = (userData?.email || "").toLowerCase().trim();
    const membershipAccountIds = getCustomerMembershipAccountIds(membership, userData);

    console.log("[loadCustomerAccounts] Looking for accounts:", {
      customerEmail,
      membershipAccountIds,
      accountId: userData?.accountId,
    });

    // Step 1: Get primary account by ID if it exists
    const primaryAccountId =
      membership?.primaryAccountId ||
      membership?.accountId ||
      userData?.accountId ||
      userData?.primaryAccountId ||
      userData?.primaryLocationId ||
      userData?.primaryCustomerAccountId ||
      null;

    for (const membershipAccountId of membershipAccountIds) {
      try {
        const membershipAccountSnap = await getDoc(
          doc(db, "companies", compId, "accounts", membershipAccountId)
        );
        if (membershipAccountSnap.exists()) {
          byId.set(membershipAccountSnap.id, {
            id: membershipAccountSnap.id,
            ...membershipAccountSnap.data(),
          });
        }
      } catch (e) {
        console.warn("[loadCustomerAccounts] Failed to fetch membership account:", e);
      }
    }

    if (primaryAccountId) {
      try {
        const snap = await getDoc(doc(db, "companies", compId, "accounts", primaryAccountId));
        if (snap.exists()) {
          byId.set(snap.id, { id: snap.id, ...snap.data() });
          console.log("[loadCustomerAccounts] Found primary account:", snap.id);
        }
      } catch (e) {
        console.warn("[loadCustomerAccounts] Failed to fetch primary account:", e);
      }
    }

    // Step 2: Query accounts explicitly linked to this customer user
    if (customerUid) {
      try {
        const colRef = collection(db, "companies", compId, "accounts");
        const ownershipQueries = [
          query(colRef, where("primaryCustomerUserId", "==", customerUid)),
          query(colRef, where("portalUserId", "==", customerUid)),
          query(colRef, where("portalUserIds", "array-contains", customerUid)),
          query(colRef, where("createdBy", "==", customerUid)),
        ];

        for (const ownershipQuery of ownershipQueries) {
          const ownershipSnap = await getDocs(ownershipQuery);
          ownershipSnap.docs.forEach((d) => {
            if (!byId.has(d.id)) {
              byId.set(d.id, { id: d.id, ...d.data() });
              console.log("[loadCustomerAccounts] Found linked account:", d.id);
            }
          });
        }
      } catch (e) {
        console.warn("[loadCustomerAccounts] Ownership queries failed:", e);
      }
    }

    // Step 3: Legacy fallback by email for older account records
    if (customerEmail) {
      try {
        const colRef = collection(db, "companies", compId, "accounts");

        const emailQueries = [
          query(colRef, where("contactEmail", "==", customerEmail)),
          query(colRef, where("email", "==", customerEmail)),
          query(colRef, where("primaryContact.email", "==", customerEmail)),
        ];

        for (const emailQuery of emailQueries) {
          const emailSnap = await getDocs(emailQuery);
          emailSnap.docs.forEach((d) => {
            if (!byId.has(d.id)) {
              byId.set(d.id, { id: d.id, ...d.data() });
              console.log("[loadCustomerAccounts] Found legacy email-linked account:", d.id);
            }
          });
        }
      } catch (e) {
        console.warn("[loadCustomerAccounts] Email queries failed:", e);
      }
    }

    const list = Array.from(byId.values());
    console.log(`[loadCustomerAccounts] Found ${list.length} account(s)`);

    // Sort with primary first, then alphabetically
    const primaryId = primaryAccountId || "";
    list.sort((a, b) => {
      const aP = primaryId && a?.id === primaryId ? 1 : 0;
      const bP = primaryId && b?.id === primaryId ? 1 : 0;
      if (aP !== bP) return bP - aP;

      const an = (a?.name || a?.businessName || "").toLowerCase();
      const bn = (b?.name || b?.businessName || "").toLowerCase();
      return an.localeCompare(bn);
    });

    return list;
  } catch (err) {
    console.error("[CustomerSampleScreen] loadCustomerAccounts error:", err);
    return [];
  }
}

/**
 * ✅ STANDARDIZED: Customer sample orders loader
 * Uses createdBy (NEW standard)
 */
async function loadCustomerSampleOrders(db, compId, userId, accounts = []) {
  try {
    const colRef = collection(db, "companies", compId, "sampleOrders");
    const orderMap = new Map();
    const visibleAccountIds = Array.from(
      new Set(
        (Array.isArray(accounts) ? accounts : [])
          .map((account) => String(account?.id || "").trim())
          .filter(Boolean)
      )
    );

    const chunk = (arr, size = 10) => {
      const chunks = [];
      for (let i = 0; i < arr.length; i += size) {
        chunks.push(arr.slice(i, i + size));
      }
      return chunks;
    };

    const collect = (snap) => {
      snap.docs.forEach((d) => {
        orderMap.set(d.id, { id: d.id, ...d.data() });
      });
    };

    if (userId) {
      const createdBySnap = await getDocs(query(colRef, where("createdBy", "==", userId)));
      collect(createdBySnap);
    }

    for (const ids of chunk(visibleAccountIds)) {
      const [accountSnap, locationSnap, accountIdsSnap] = await Promise.all([
        getDocs(query(colRef, where("accountId", "in", ids))),
        getDocs(query(colRef, where("locationId", "in", ids))),
        getDocs(query(colRef, where("accountIds", "array-contains-any", ids))),
      ]);

      collect(accountSnap);
      collect(locationSnap);
      collect(accountIdsSnap);
    }

    return Array.from(orderMap.values()).sort(
      (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
    );
  } catch (err) {
    console.error("[CustomerSampleScreen] loadCustomerSampleOrders error:", err);
    return [];
  }
}

/**
 * ✅ Customer-safe submit
 * ✅ Writes NEW standardized fields (createdBy*)
 */
async function submitCustomerSampleRequest({
  db,
  companyId,
  firebaseUser,
  userProfile,
  selectedAccount,
  requestedDate,
  notes,
  validItems,
  inventory,
  groupMode,
  kitId,
}) {
  const createdBy = firebaseUser.uid;
  const createdByName =
    userProfile?.fullName || firebaseUser.displayName || firebaseUser.email || "";
  const createdByRole = (userProfile?.role || "customer").toLowerCase();

  const accountId = selectedAccount?.id;
  const accountName =
    selectedAccount?.name ||
    selectedAccount?.businessName ||
    selectedAccount?.accountName ||
    "";
  const assignedToId =
    selectedAccount?.assignedToId ||
    selectedAccount?.assignedUserId ||
    selectedAccount?.repUserId ||
    selectedAccount?.salesRepId ||
    null;
  const assignedToName =
    selectedAccount?.assignedToName ||
    selectedAccount?.repUserName ||
    selectedAccount?.salesRepName ||
    null;

  // Enrich items
  const invMap = new Map((inventory || []).map((i) => [i.id, i]));
  const items = validItems.map((it) => {
    const inv = invMap.get(it.productId);
    return {
      productId: it.productId,
      quantity: it.quantity,
      // ✅ Keep both "name" and "productName" for UI compatibility
      name: inv?.name || inv?.title || null,
      productName: inv?.name || inv?.title || null,
      sku: inv?.sku || null,
      size: inv?.size || null,
      uom: inv?.uom || null,
    };
  });

  const totalQty = items.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0);

  const payload = {
    // ✅ NEW standard identity fields
    createdBy,
    createdByName,
    createdByRole,

    accountId,
    companyId,
    accountName: accountName || null,
    assignedToId: assignedToId || null,
    assignedUserId: assignedToId || null,
    assignedToName: assignedToName || null,

    requestedDate: requestedDate || null,
    deliveryDate: null,
    notes: notes || "",
    status: "Pending",

    items,
    itemCount: items.length,
    quantity: totalQty,
    groupMode: groupMode || "single",
    kitId: kitId || null,

    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const colRef = collection(db, "companies", companyId, "sampleOrders");
  const docRef = await addDoc(colRef, payload);

  return { id: docRef.id, payload };
}

const CustomerSampleScreen = () => {
  const location = useLocation();
  const auth = getAuth();
  const db = getFirestore();

  const [firebaseUser, setFirebaseUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [customerMemberships, setCustomerMemberships] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [company, setCompany] = useState(null);
  const [repUser, setRepUser] = useState(null);

  const [accounts, setAccounts] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [sampleOrders, setSampleOrders] = useState([]);

  const [primaryAccountId, setPrimaryAccountId] = useState("");
  const [settingPrimary, setSettingPrimary] = useState(false);

  const [showAddLocation, setShowAddLocation] = useState(false);
  const [addLocationSaving, setAddLocationSaving] = useState(false);
  const [newLocation, setNewLocation] = useState({
    name: "",
    street: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
  });

  const [form, setForm] = useState({
    productId: "",
    quantity: 1,
    requestedDate: "",
    deliveryDate: "",
    notes: "",
    items: [],
    accountId: "",
    accountName: "",
    kitId: "",
    groupMode: "single",
  });

  const [profileForm, setProfileForm] = useState({
    fullName: "",
    phone: "",
  });
  const [profileEditing, setProfileEditing] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);

  const [verificationSending, setVerificationSending] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState("");
  const [feedbackSavingId, setFeedbackSavingId] = useState("");
  const [locationSavingId, setLocationSavingId] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [activeTab, setActiveTab] = useState("request");

  const cart = useSampleOrderCart({
    distributorId: companyId || "pending_company",
    currentUserId: firebaseUser?.uid || "pending_user",
  });

  // ---------------------------------------------------
  // Data loaders
  // ---------------------------------------------------
  const loadCompany = async (dbx, compId) => {
    try {
      const snap = await getDoc(doc(dbx, "companies", compId));
      return snap.exists() ? { id: snap.id, ...snap.data() } : null;
    } catch (err) {
      console.error("[CustomerSampleScreen] Error loading company:", err);
      return null;
    }
  };

  const loadAssignedRepUser = async (dbx, { account, userData }) => {
    try {
      const nestedRep =
        account?.assignedTo ||
        account?.assignedUser ||
        account?.repUser ||
        account?.salesRep ||
        null;

      const accountRepId =
        account?.assignedToId ||
        account?.assignedUserId ||
        account?.repUserId ||
        account?.salesRepId ||
        nestedRep?.id ||
        nestedRep?.uid ||
        nestedRep?.userId ||
        null;

      const fallbackRepId = userData?.repUserId || null;
      const resolvedRepId = accountRepId || fallbackRepId;

      if (resolvedRepId) {
        const repSnap = await getDoc(doc(dbx, "users", String(resolvedRepId)));
        if (repSnap.exists()) {
          return { id: repSnap.id, ...repSnap.data() };
        }
      }

      const accountRepName =
        account?.assignedToName ||
        account?.repUserName ||
        account?.salesRepName ||
        nestedRep?.fullName ||
        nestedRep?.displayName ||
        nestedRep?.name ||
        null;

      const accountRepEmail =
        account?.assignedToEmail ||
        account?.repUserEmail ||
        account?.salesRepEmail ||
        nestedRep?.email ||
        "";

      const accountRepPhone =
        account?.assignedToPhone ||
        account?.repUserPhone ||
        account?.salesRepPhone ||
        nestedRep?.phone ||
        nestedRep?.mobilePhone ||
        "";

      if (resolvedRepId || accountRepName || userData?.repUserName || userData?.repUserEmail) {
        return {
          id: resolvedRepId || null,
          fullName: accountRepName || userData?.repUserName || "",
          email: accountRepEmail || userData?.repUserEmail || "",
          phone: accountRepPhone || userData?.repUserPhone || "",
        };
      }

      return null;
    } catch (err) {
      console.warn("[CustomerSampleScreen] Error loading assigned rep:", err);
      return null;
    }
  };

  const loadInventory = async (dbx, compId) => {
    try {
      const colRef = collection(dbx, "companies", compId, "inventory");

      // ✅ SIMPLIFIED: Let Firestore rules handle filtering
      const snap = await getDocs(colRef);

      return snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((item) => item.sampleable === true && item.customerVisible === true)
        .sort((a, b) =>
          (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase())
        );
    } catch (err) {
      console.error("[CustomerSampleScreen] Error loading inventory:", err);

      const code = String(err?.code || "");
      if (code === "permission-denied") {
        setError(
          "Your distributor's sample catalog is currently hidden. Ask them to mark inventory items as Sample-able AND Visible to Customers."
        );
      } else {
        setError("Could not load the sample catalog. Please try again.");
      }

      return [];
    }
  };

  const loadCustomerContext = useCallback(
    async ({ user, profile, memberships }) => {
      const hydratedProfile = hydrateCustomerProfileWithMemberships({
        uid: user.uid,
        profile,
        memberships,
      });

      const activeMembership = hydratedProfile.activeCustomerMembership || null;
      const activeCompanyId = hydratedProfile.companyId || "";
      if (!activeCompanyId) {
        throw new Error("Your customer profile is missing an active company.");
      }

      const [companyData, accountsData, inv] = await Promise.all([
        loadCompany(db, activeCompanyId),
        loadCustomerAccounts(db, activeCompanyId, hydratedProfile, activeMembership),
        loadInventory(db, activeCompanyId),
      ]);

      const reqs = await loadCustomerSampleOrders(db, activeCompanyId, user.uid, accountsData);

      return {
        hydratedProfile,
        activeCompanyId,
        activeMembership,
        companyData,
        accountsData,
        inventoryData: inv,
        sampleOrdersData: reqs,
      };
    },
    [db]
  );

  // ---------------------------------------------------
  // Load auth + profile + related data
  // ---------------------------------------------------
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setLoading(true);
      setError("");
      setVerificationMessage("");

      if (!user) {
        setFirebaseUser(null);
        setUserProfile(null);
        setCustomerMemberships([]);
        setCompanyId(null);
        setCompany(null);
        setRepUser(null);
        setAccounts([]);
        setInventory([]);
        setSampleOrders([]);
        setPrimaryAccountId("");
        setLoading(false);
        return;
      }

      setFirebaseUser(user);

      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) {
          setError("User profile not found.");
          setLoading(false);
          return;
        }

        const userData = { uid: user.uid, ...userSnap.data() };
        const memberships = await loadCustomerMemberships(db, user.uid);
        setCustomerMemberships(memberships);

        const {
          hydratedProfile,
          activeCompanyId,
          companyData,
          accountsData,
          inventoryData,
          sampleOrdersData,
        } = await loadCustomerContext({
          user,
          profile: userData,
          memberships,
        });

        setUserProfile(hydratedProfile);

        setProfileForm({
          fullName: hydratedProfile.fullName || "",
          phone: hydratedProfile.phone || "",
        });

        const primaryIdFromProfile =
          hydratedProfile.primaryAccountId ||
          hydratedProfile.primaryLocationId ||
          hydratedProfile.primaryCustomerAccountId ||
          hydratedProfile.accountId ||
          "";

        setPrimaryAccountId(primaryIdFromProfile);

        setCompanyId(activeCompanyId);
        setStoredActiveCustomerCompany({ uid: user.uid, companyId: activeCompanyId });

        setCompany(companyData);
        setAccounts(accountsData);
        setInventory(inventoryData);
        setSampleOrders(sampleOrdersData);

        // Auto-set primary if only one account
        let uiPrimaryId = primaryIdFromProfile;
        if (!uiPrimaryId && accountsData.length === 1) {
          uiPrimaryId = accountsData[0].id;
          setPrimaryAccountId(uiPrimaryId);
        }

        // Auto-select default account in form
        const defaultAcc = pickMyAccount({
          accounts: accountsData,
          userProfile: {
            ...hydratedProfile,
            primaryAccountId: uiPrimaryId || hydratedProfile?.primaryAccountId || "",
          },
        });

        if (defaultAcc?.id) {
          setForm((prev) => ({
            ...prev,
            accountId: prev.accountId || defaultAcc.id,
            accountName: prev.accountName || defaultAcc.name || defaultAcc.businessName || "",
          }));
        }

        const resolvedRep = await loadAssignedRepUser(db, {
          account: defaultAcc,
          userData: hydratedProfile,
        });
        setRepUser(resolvedRep);

        // Stamp emailVerified if needed
        if (user.emailVerified && !hydratedProfile.emailVerifiedOn) {
          await updateDoc(userRef, {
            emailVerified: true,
            emailVerifiedOn: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });

          setUserProfile((prev) => ({
            ...(prev || {}),
            emailVerified: true,
            emailVerifiedOn: { seconds: Math.floor(Date.now() / 1000) },
          }));
        }
      } catch (err) {
        console.error("[CustomerSampleScreen] Error loading data:", err);
        setError("Failed to load your account. Please try again.");
      }

      setLoading(false);
    });

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------
  // Derived
  // ---------------------------------------------------
  const isCustomer = userProfile?.role === "customer";
  const isVerified = !!firebaseUser?.emailVerified || !!userProfile?.emailVerified;
  const verifiedOnLabel = formatFirestoreTimestamp(userProfile?.emailVerifiedOn);

  const catalogEnabled = !!(
    company?.featureFlags?.customerCatalogEnabled ?? company?.customerCatalogEnabled
  );
  const requestedView = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    const view = (params.get("view") || "").trim().toLowerCase();
    return view === "company" ? "company" : view === "profile" ? "profile" : "";
  }, [location.search]);
  const visibleAccountIds = useMemo(
    () => accounts.map((account) => account.id).filter(Boolean),
    [accounts]
  );

  useEffect(() => {
    if (!companyId || !firebaseUser?.uid) return undefined;

    const colRef = collection(db, "companies", companyId, "sampleOrders");
    const orderMap = new Map();
    const unsubscribers = [];

    const chunkIds = (arr, size = 10) => {
      const chunks = [];
      for (let i = 0; i < arr.length; i += size) chunks.push(arr.slice(i, i + size));
      return chunks;
    };

    const applySnapshot = (snap) => {
      snap.docs.forEach((d) => {
        orderMap.set(d.id, { id: d.id, ...d.data() });
      });

      setSampleOrders(
        Array.from(orderMap.values()).sort(
          (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
        )
      );
    };

    unsubscribers.push(
      onSnapshot(
        query(colRef, where("createdBy", "==", firebaseUser.uid)),
        applySnapshot,
        (err) => console.error("[CustomerSampleScreen] sampleOrders createdBy subscription error:", err)
      )
    );

    for (const ids of chunkIds(visibleAccountIds)) {
      if (!ids.length) continue;
      unsubscribers.push(
        onSnapshot(
          query(colRef, where("accountId", "in", ids)),
          applySnapshot,
          (err) => console.error("[CustomerSampleScreen] sampleOrders accountId subscription error:", err)
        )
      );
      unsubscribers.push(
        onSnapshot(
          query(colRef, where("locationId", "in", ids)),
          applySnapshot,
          (err) => console.error("[CustomerSampleScreen] sampleOrders locationId subscription error:", err)
        )
      );
      unsubscribers.push(
        onSnapshot(
          query(colRef, where("accountIds", "array-contains-any", ids)),
          applySnapshot,
          (err) => console.error("[CustomerSampleScreen] sampleOrders accountIds subscription error:", err)
        )
      );
    }

    return () => {
      unsubscribers.forEach((unsubscribe) => {
        try {
          unsubscribe();
        } catch {}
      });
    };
  }, [companyId, db, firebaseUser?.uid, visibleAccountIds]);

  useEffect(() => {
    if (!catalogEnabled && activeTab === "catalog") {
      setActiveTab("request");
    }
  }, [catalogEnabled, activeTab]);

  useEffect(() => {
    if (requestedView) {
      setActiveTab("profile");
    }
  }, [requestedView]);

  const myAccount = useMemo(() => {
    return pickMyAccount({
      accounts,
      userProfile: {
        ...(userProfile || {}),
        primaryAccountId: primaryAccountId || userProfile?.primaryAccountId || "",
      },
    });
  }, [accounts, userProfile, primaryAccountId]);

  useEffect(() => {
    let cancelled = false;

    const refreshRep = async () => {
      if (!companyId || !userProfile) {
        setRepUser(null);
        return;
      }

      const resolved = await loadAssignedRepUser(db, {
        account: myAccount,
        userData: userProfile,
      });

      if (!cancelled) {
        setRepUser(resolved);
      }
    };

    refreshRep();

    return () => {
      cancelled = true;
    };
  }, [db, companyId, myAccount, userProfile]);

  // ---------------------------------------------------
  // Cart -> Form sync
  // ---------------------------------------------------
  const cartSignatureRef = useRef("");

  const cartCleanItems = useMemo(() => {
    const arr = Array.isArray(cart?.itemsArray) ? cart.itemsArray : [];
    return arr
      .filter((i) => i?.productId && Number(i.quantity) > 0)
      .map((i) => ({
        productId: String(i.productId),
        quantity: Math.max(1, Number(i.quantity) || 1),
      }));
  }, [cart?.itemsArray]);

  const cartStableSignature = useMemo(() => {
    const stable = [...cartCleanItems].sort((a, b) => a.productId.localeCompare(b.productId));
    return JSON.stringify(stable);
  }, [cartCleanItems]);

  useEffect(() => {
    if (cartStableSignature === cartSignatureRef.current) return;
    cartSignatureRef.current = cartStableSignature;

    setForm((prev) => {
      const prevItems = Array.isArray(prev?.items) ? prev.items : [];
      const prevClean = prevItems
        .filter((i) => i?.productId && Number(i.quantity) > 0)
        .map((i) => ({
          productId: String(i.productId),
          quantity: Math.max(1, Number(i.quantity) || 1),
        }));

      const cartMap = new Map(cartCleanItems.map((i) => [i.productId, i.quantity]));
      const seen = new Set();
      const merged = [];

      prevClean.forEach((it) => {
        if (cartMap.has(it.productId)) {
          merged.push({ productId: it.productId, quantity: cartMap.get(it.productId) });
          seen.add(it.productId);
        }
      });

      cartCleanItems.forEach((it) => {
        if (!seen.has(it.productId)) {
          merged.push({ productId: it.productId, quantity: it.quantity });
          seen.add(it.productId);
        }
      });

      const next = { ...prev, items: merged };

      if (!next.productId && merged.length) {
        next.productId = merged[0].productId;
        next.quantity = merged[0].quantity;
      }
      if (merged.length === 0) {
        next.productId = "";
        next.quantity = 1;
      }

      return next;
    });
  }, [cartStableSignature, cartCleanItems]);

  // ---------------------------------------------------
  // Primary location setter
  // ---------------------------------------------------
  const handleSetPrimaryLocation = async (accountId) => {
    if (!firebaseUser || !companyId || !accountId) return;

    setSettingPrimary(true);
    setError("");

    try {
      await updateDoc(doc(db, "users", firebaseUser.uid), {
        primaryAccountId: accountId,
        accountId: accountId,
        updatedAt: serverTimestamp(),
      });

      await upsertCustomerMembership({
        db,
        uid: firebaseUser.uid,
        companyId,
        companyName: company?.businessName || company?.name || userProfile?.companyName || "",
        accountId,
        accountName:
          accounts.find((account) => account.id === accountId)?.name ||
          accounts.find((account) => account.id === accountId)?.businessName ||
          "",
        accountIds: accounts.map((account) => account.id).filter(Boolean),
        repUserId: userProfile?.repUserId || null,
        role: "customer",
        metadata: {
          fullName: userProfile?.fullName || firebaseUser.displayName || "",
          email: userProfile?.email || firebaseUser.email || "",
        },
      });

      const nextAccountIds = accounts.map((account) => account.id).filter(Boolean);
      const nextActiveMembership = {
        ...(userProfile?.activeCustomerMembership || {}),
        companyId,
        companyName: company?.businessName || company?.name || userProfile?.companyName || "",
        primaryAccountId: accountId,
        accountId,
        accountName:
          accounts.find((account) => account.id === accountId)?.name ||
          accounts.find((account) => account.id === accountId)?.businessName ||
          "",
        accountIds: nextAccountIds,
        repUserId: userProfile?.repUserId || null,
        role: "customer",
      };

      setPrimaryAccountId(accountId);
      setUserProfile((prev) => ({
        ...(prev || {}),
        primaryAccountId: accountId,
        accountId: accountId,
        customerAccountIds: nextAccountIds,
        activeCustomerMembership: nextActiveMembership,
        customerMemberships: Array.isArray(prev?.customerMemberships)
          ? prev.customerMemberships.map((membership) =>
              membership?.companyId === companyId ? nextActiveMembership : membership
            )
          : prev?.customerMemberships,
      }));
      setCustomerMemberships((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        const exists = list.some((membership) => membership?.companyId === companyId);
        return exists
          ? list.map((membership) => (membership?.companyId === companyId ? nextActiveMembership : membership))
          : [...list, nextActiveMembership];
      });

      setForm((prev) => ({
        ...prev,
        accountId: accountId,
      }));
    } catch (err) {
      console.error("[CustomerSampleScreen] set primary location error:", err);
      setError("Failed to set primary location. Please try again.");
    } finally {
      setSettingPrimary(false);
    }
  };

  const handleSwitchCompany = async (nextCompanyId) => {
    if (!firebaseUser || !userProfile || !nextCompanyId || nextCompanyId === companyId) return;

    setLoading(true);
    setError("");

    try {
      setStoredActiveCustomerCompany({ uid: firebaseUser.uid, companyId: nextCompanyId });

      const nextMemberships = await loadCustomerMemberships(db, firebaseUser.uid);
      setCustomerMemberships(nextMemberships);

      const {
        hydratedProfile,
        activeCompanyId,
        companyData,
        accountsData,
        inventoryData,
        sampleOrdersData,
      } = await loadCustomerContext({
        user: firebaseUser,
        profile: userProfile,
        memberships: nextMemberships,
      });

      setUserProfile(hydratedProfile);
      setCompanyId(activeCompanyId);
      setCompany(companyData);
      setAccounts(accountsData);
      setInventory(inventoryData);
      setSampleOrders(sampleOrdersData);

      const nextPrimaryAccountId =
        hydratedProfile.primaryAccountId ||
        hydratedProfile.accountId ||
        accountsData[0]?.id ||
        "";
      setPrimaryAccountId(nextPrimaryAccountId);

      const defaultAcc = pickMyAccount({
        accounts: accountsData,
        userProfile: hydratedProfile,
      });

      setForm({
        productId: "",
        quantity: 1,
        requestedDate: "",
        deliveryDate: "",
        notes: "",
        items: [],
        accountId: defaultAcc?.id || nextPrimaryAccountId,
        accountName: defaultAcc?.name || defaultAcc?.businessName || "",
        kitId: "",
        groupMode: "single",
      });

      try {
        cart?.clear?.();
      } catch (clearErr) {
        console.warn("[CustomerSampleScreen] cart.clear failed during company switch:", clearErr);
      }

      setActiveTab("request");
    } catch (err) {
      console.error("[CustomerSampleScreen] switch company error:", err);
      setError(err?.message || "Failed to switch companies. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------
  // Form handlers
  // ---------------------------------------------------
  const handleFormChange = (field, value) => {
    if (field === "items") {
      const nextItems = Array.isArray(value)
        ? value
            .filter((i) => i?.productId && Number(i.quantity) > 0)
            .map((i) => ({
              productId: String(i.productId),
              quantity: Math.max(1, Number(i.quantity) || 1),
            }))
        : [];

      try {
        const desired = new Map(nextItems.map((i) => [i.productId, i.quantity]));

        const existingIds = Object.keys(cart?.cartMap || {});
        existingIds.forEach((pid) => {
          if (!desired.has(pid)) cart?.remove?.(pid);
        });

        desired.forEach((qty, pid) => {
          const current = cart?.getQty?.(pid) || 0;
          if (current !== qty) cart?.setQty?.(pid, qty);
        });
      } catch (e) {
        console.warn("[CustomerSampleScreen] failed syncing items -> cart", e);
      }

      setForm((prev) => ({ ...prev, items: nextItems }));
      return;
    }

    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearForm = () => {
    const keepAccountId = form.accountId || "";
    const keepAccountName = form.accountName || "";

    const ok = window.confirm(
      "Clear this sample order?\n\nThis will remove all items, requested date, kit selection, and notes."
    );
    if (!ok) return;

    setError("");

    try {
      cart?.clear?.();
    } catch (e) {
      console.warn("[CustomerSampleScreen] cart.clear failed:", e);
    }

    setForm({
      productId: "",
      quantity: 1,
      requestedDate: "",
      deliveryDate: "",
      notes: "",
      items: [],
      accountId: keepAccountId,
      accountName: keepAccountName,
      kitId: "",
      groupMode: "single",
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ---------------------------------------------------
  // Submit
  // ---------------------------------------------------
  const handleSubmit = async (e, options) => {
    e.preventDefault();

    if (!companyId || !firebaseUser) return;

    if (!isVerified) {
      setError("Please verify your email before submitting sample requests.");
      return;
    }

    const rawItems =
      Array.isArray(options?.items) && options.items.length > 0
        ? options.items
        : Array.isArray(form.items) && form.items.length > 0
        ? form.items
        : cart?.itemsArray?.length
        ? cart.itemsArray
        : [{ productId: form.productId, quantity: form.quantity }];

    const validItems = (rawItems || [])
      .filter((i) => i && i.productId && Number(i.quantity) > 0)
      .map((i) => ({
        productId: String(i.productId),
        quantity: Math.max(1, Number(i.quantity) || 1),
      }));

    if (validItems.length === 0) {
      setError("Please add at least one product to sample.");
      return;
    }

    const maxItemsPerOrder =
      typeof company?.featureFlags?.maxItemsPerOrder === "number" &&
      company.featureFlags.maxItemsPerOrder > 0
        ? company.featureFlags.maxItemsPerOrder
        : null;

    if (maxItemsPerOrder && validItems.length > maxItemsPerOrder) {
      setError(`This distributor allows up to ${maxItemsPerOrder} items per sample order.`);
      return;
    }

    const requestedDate = options?.requestedDate || form.requestedDate;
    if (!requestedDate) {
      setError("Please choose a valid requested delivery date.");
      return;
    }

    const requireReasonForSample = !!company?.featureFlags?.requireReasonForSample;
    const trimmedNotes = String(options?.notes ?? form.notes ?? "").trim();
    if (requireReasonForSample && !trimmedNotes) {
      setError("Please share why you need this sample before submitting.");
      return;
    }

    setError("");
    setSaving(true);

    try {
      const submittedAccountId = options?.accountId || form.accountId || "";

      const selectedAccount =
        (submittedAccountId && accounts.find((a) => a.id === submittedAccountId)) || myAccount;

      if (!selectedAccount?.id) {
        throw new Error("Please select a location/account before submitting.");
      }

      const groupMode = options?.groupMode || form.groupMode || "single";
      const kitId = options?.kitId || form.kitId || null;
      const notes = trimmedNotes;
      const duplicateBlockEnabled = !!company?.featureFlags?.blockDuplicateRequestsEnabled;
      const duplicateBlockDays = company?.featureFlags?.blockDuplicateDays;

      if (duplicateBlockEnabled) {
        const duplicate = findDuplicateSampleRequest({
          sampleOrders,
          accountId: selectedAccount.id,
          productIds: validItems.map((item) => item.productId),
          windowDays: duplicateBlockDays,
        });

        if (duplicate) {
          const duplicateNames =
            duplicate.duplicateItemNames.length > 0
              ? duplicate.duplicateItemNames.join(", ")
              : "one or more of these items";
          throw new Error(
            `You already requested ${duplicateNames} for ${selectedAccount.name || "this account"} within the last ${duplicateBlockDays} days.`
          );
        }
      }

      await submitCustomerSampleRequest({
        db,
        companyId,
        firebaseUser,
        userProfile,
        selectedAccount,
        requestedDate,
        notes,
        validItems,
        inventory,
        groupMode,
        kitId,
      });

      // Clear cart + reset form
      const keepAccountId = form.accountId || "";
      const keepAccountName = form.accountName || "";

      try {
        cart?.clear?.();
      } catch (e) {
        console.warn("[CustomerSampleScreen] cart.clear failed:", e);
      }

      setForm({
        productId: "",
        quantity: 1,
        requestedDate: "",
        deliveryDate: "",
        notes: "",
        items: [],
        accountId: keepAccountId,
        accountName: keepAccountName,
        kitId: "",
        groupMode: "single",
      });

      // Refresh requests
      const refreshed = await loadCustomerSampleOrders(db, companyId, firebaseUser.uid, accounts);
      setSampleOrders(refreshed);

      setActiveTab("orders");
    } catch (err) {
      console.error("[CustomerSampleScreen] submit error:", err);
      setError(err?.message || "Could not create sample request.");
    }

    setSaving(false);
  };

  // ---------------------------------------------------
  // Verification
  // ---------------------------------------------------
  const handleSendVerification = async () => {
    if (!auth.currentUser) return;

    setVerificationSending(true);
    setVerificationMessage("");
    setError("");

    try {
      await auth.currentUser.reload();

      if (auth.currentUser.emailVerified) {
        setVerificationMessage("You're already verified.");
        return;
      }

      await sendEmailVerification(auth.currentUser, {
        url: getContinueUrlForEmailVerification(),
        handleCodeInApp: true,
      });

      setVerificationMessage("Verification email sent. Click the link, then refresh this page.");

      await updateDoc(doc(db, "users", auth.currentUser.uid), {
        verificationSentAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error("[CustomerSampleScreen] verification error:", err);
      setError(err?.message || "Failed to send verification email.");
    } finally {
      setVerificationSending(false);
    }
  };

  // ---------------------------------------------------
  // Add location
  // ---------------------------------------------------
  const handleAddLocationSubmit = async () => {
    if (!companyId || !firebaseUser) return;

    if ((accounts || []).length >= 5) {
      setError(
        "You've reached the maximum of 5 locations. Please contact your distributor if you need more."
      );
      return;
    }

    setAddLocationSaving(true);
    setError("");

    try {
      const accountsRef = collection(db, "companies", companyId, "accounts");

      const trimmedName = newLocation.name.trim();
      const trimmedStreet = newLocation.street.trim();
      const trimmedCity = newLocation.city.trim();
      const trimmedState = newLocation.state.trim();
      const trimmedZip = newLocation.zip.trim();
      const trimmedPhone = newLocation.phone.trim();

      const contactName = profileForm.fullName || userProfile?.fullName || "";
      const contactEmail = userProfile?.email || firebaseUser.email || "";
      const bestPhone = trimmedPhone || profileForm.phone || userProfile?.phone || "";

      const staffGroupId = company?.staffGroupId || null;
      const groupIds = staffGroupId ? [staffGroupId] : [];

      const newAccountRef = await addDoc(accountsRef, {
        name: trimmedName,
        addressStreet: trimmedStreet || null,
        addressCity: trimmedCity || null,
        addressState: trimmedState || null,
        addressZip: trimmedZip || null,
        street: trimmedStreet || null,
        city: trimmedCity || null,
        state: trimmedState || null,
        zip: trimmedZip || null,
        phone: bestPhone || null,
        primaryContact: {
          name: contactName || null,
          title: null,
          email: contactEmail || null,
          phone: bestPhone || null,
        },
        contactName: contactName || null,
        contact: contactName || null,
        email: contactEmail || null,
        contactEmail: contactEmail || null,
        primaryCustomerUserId: firebaseUser.uid,
        portalUserId: firebaseUser.uid,
        createdBy: firebaseUser.uid,
        groupIds,
        active: true,
        stage: "Sampling",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await upsertCustomerMembership({
        db,
        uid: firebaseUser.uid,
        companyId,
        companyName: company?.businessName || company?.name || userProfile?.companyName || "",
        accountId: newAccountRef.id,
        accountName: trimmedName,
        accountIds: [...accounts.map((account) => account.id).filter(Boolean), newAccountRef.id],
        repUserId: userProfile?.repUserId || null,
        role: "customer",
        metadata: {
          fullName: userProfile?.fullName || firebaseUser.displayName || "",
          email: userProfile?.email || firebaseUser.email || "",
        },
      });

      const nextAccountIds = [...accounts.map((account) => account.id).filter(Boolean), newAccountRef.id];
      const nextActiveMembership = {
        ...(userProfile?.activeCustomerMembership || {}),
        companyId,
        companyName: company?.businessName || company?.name || userProfile?.companyName || "",
        primaryAccountId:
          userProfile?.activeCustomerMembership?.primaryAccountId ||
          userProfile?.primaryAccountId ||
          userProfile?.accountId ||
          "",
        accountId:
          userProfile?.activeCustomerMembership?.accountId ||
          userProfile?.accountId ||
          "",
        accountName:
          userProfile?.activeCustomerMembership?.accountName ||
          "",
        accountIds: nextAccountIds,
        repUserId: userProfile?.repUserId || null,
        role: "customer",
      };

      // Reload accounts + sample orders
      const refreshedAccounts = await loadCustomerAccounts(
        db,
        companyId,
        {
          ...(userProfile || {}),
          customerAccountIds: nextAccountIds,
          activeCustomerMembership: nextActiveMembership,
        },
        nextActiveMembership
      );
      const refreshedReqs = await loadCustomerSampleOrders(
        db,
        companyId,
        firebaseUser.uid,
        refreshedAccounts
      );

      setAccounts(refreshedAccounts);
      setSampleOrders(refreshedReqs);
      setUserProfile((prev) => ({
        ...(prev || {}),
        customerAccountIds: nextAccountIds,
        activeCustomerMembership: nextActiveMembership,
        customerMemberships: Array.isArray(prev?.customerMemberships)
          ? prev.customerMemberships.map((membership) =>
              membership?.companyId === companyId ? nextActiveMembership : membership
            )
          : prev?.customerMemberships,
      }));
      setCustomerMemberships((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        const exists = list.some((membership) => membership?.companyId === companyId);
        return exists
          ? list.map((membership) => (membership?.companyId === companyId ? nextActiveMembership : membership))
          : [...list, nextActiveMembership];
      });

      // If no primary exists, set the first refreshed as primary
      const hasPrimary =
        !!primaryAccountId ||
        !!userProfile?.primaryAccountId ||
        !!userProfile?.primaryLocationId ||
        !!userProfile?.primaryCustomerAccountId;

      if (!hasPrimary && refreshedAccounts.length) {
        const nextPrimary = refreshedAccounts[0].id;

        await updateDoc(doc(db, "users", firebaseUser.uid), {
          primaryAccountId: nextPrimary,
          accountId: nextPrimary,
          updatedAt: serverTimestamp(),
        });

        setPrimaryAccountId(nextPrimary);
        setUserProfile((prev) => ({
          ...(prev || {}),
          primaryAccountId: nextPrimary,
          accountId: nextPrimary,
        }));
      }

      setNewLocation({ name: "", street: "", city: "", state: "", zip: "", phone: "" });
      setShowAddLocation(false);
    } catch (err) {
      console.error("[CustomerSampleScreen] add location error:", err);
      setError("Failed to add the new location. Please try again.");
    } finally {
      setAddLocationSaving(false);
    }
  };

  const handleProfileSave = async () => {
    if (!firebaseUser) return;
    setProfileSaving(true);
    setError("");

    try {
      await updateDoc(doc(db, "users", firebaseUser.uid), {
        fullName: profileForm.fullName?.trim() || "",
        phone: profileForm.phone?.trim() || "",
        updatedAt: serverTimestamp(),
      });

      setUserProfile((prev) => ({
        ...(prev || {}),
        fullName: profileForm.fullName,
        phone: profileForm.phone,
      }));

      setProfileEditing(false);
    } catch (err) {
      console.error("[CustomerSampleScreen] save profile error:", err);
      setError("Failed to save your profile. Please try again.");
    } finally {
      setProfileSaving(false);
    }
  };

  const handleSaveLocation = async (accountId, locationDraft) => {
    if (!companyId || !firebaseUser || !accountId) return;

    setLocationSavingId(accountId);
    setError("");

    try {
      const trimmedName = (locationDraft?.name || "").trim();
      const trimmedStreet = (locationDraft?.street || "").trim();
      const trimmedCity = (locationDraft?.city || "").trim();
      const trimmedState = (locationDraft?.state || "").trim();
      const trimmedZip = (locationDraft?.zip || "").trim();
      const trimmedPhone = (locationDraft?.phone || "").trim();

      if (!trimmedName || !trimmedStreet || !trimmedCity || !trimmedState) {
        throw new Error("Please complete the location name and full address.");
      }

      const accountRef = doc(db, "companies", companyId, "accounts", accountId);

      await updateDoc(accountRef, {
        name: trimmedName,
        addressStreet: trimmedStreet || null,
        addressCity: trimmedCity || null,
        addressState: trimmedState || null,
        addressZip: trimmedZip || null,
        street: trimmedStreet || null,
        city: trimmedCity || null,
        state: trimmedState || null,
        zip: trimmedZip || null,
        phone: trimmedPhone || null,
        contactName: profileForm.fullName?.trim() || userProfile?.fullName || null,
        contact: profileForm.fullName?.trim() || userProfile?.fullName || null,
        contactEmail: userProfile?.email || firebaseUser.email || null,
        primaryContact: {
          name: profileForm.fullName?.trim() || userProfile?.fullName || null,
          email: userProfile?.email || firebaseUser.email || null,
          phone: trimmedPhone || profileForm.phone?.trim() || userProfile?.phone || null,
        },
        updatedAt: serverTimestamp(),
      });

      const refreshedAccounts = await loadCustomerAccounts(db, companyId, userProfile || {});
      setAccounts(refreshedAccounts);
    } catch (err) {
      console.error("[CustomerSampleScreen] save location error:", err);
      setError(err?.message || "Failed to save this location. Please try again.");
      throw err;
    } finally {
      setLocationSavingId("");
    }
  };

  const handleSubmitFeedback = async (request, payload) => {
    if (!companyId || !request?.id) return;
    if (!firebaseUser?.uid) throw new Error("Please log in again to submit feedback.");

    setFeedbackSavingId(request.id);
    setError("");

    try {
      const rating = Number(payload?.rating) || 0;
      const comments = String(payload?.comments || "").trim();
      const useCase = String(payload?.useCase || "").trim();
      const interestLevel = String(payload?.interestLevel || "").trim() || "warm";
      const nextStep = String(payload?.nextStep || "").trim() || "follow_up";

      const feedbackLines = [
        "Customer feedback submitted",
        `Rating: ${rating}/5`,
        `Kitchen reaction: ${FEEDBACK_INTEREST_LABELS[interestLevel] || interestLevel}`,
        `Best next step: ${FEEDBACK_NEXT_STEP_LABELS[nextStep] || nextStep}`,
      ];

      if (useCase) feedbackLines.push(`Use case: ${useCase}`);
      feedbackLines.push(`Comments: ${comments || "None provided"}`);

      const feedbackText = feedbackLines.join("\n");
      const customerName =
        userProfile?.fullName ||
        firebaseUser.displayName ||
        request?.createdByName ||
        firebaseUser.email ||
        "Customer";

      await SampleOrderCommentsService.addComment(companyId, request.id, {
        text: feedbackText,
        textPlain: feedbackText,
        createdBy: firebaseUser.uid,
        createdByName: customerName,
        createdByRole: "customer",
      });

      await updateDoc(doc(db, "companies", companyId, "sampleOrders", request.id), {
        feedbackReceived: true,
        feedbackRating: rating,
        feedbackComments: comments,
        feedbackUseCase: useCase,
        feedbackInterestLevel: interestLevel,
        feedbackNextStep: nextStep,
        feedbackSubmittedAt: serverTimestamp(),
        feedbackSubmittedBy: firebaseUser.uid,
        updatedAt: serverTimestamp(),
      });

      setSampleOrders((prev) =>
        (Array.isArray(prev) ? prev : []).map((order) =>
          order?.id === request.id
            ? {
                ...order,
                feedbackRating: rating,
                feedbackComments: comments,
                feedbackUseCase: useCase,
                feedbackInterestLevel: interestLevel,
                feedbackNextStep: nextStep,
                feedbackSubmittedAt: new Date().toISOString(),
                feedbackSubmittedBy: firebaseUser.uid,
              }
            : order
        )
      );
    } catch (err) {
      console.error("[CustomerSampleScreen] feedback error:", err);
      setError(err?.message || "Failed to save feedback. Please try again.");
      throw err;
    } finally {
      setFeedbackSavingId("");
    }
  };

  const handleLogout = async () => {
    setError("");
    try {
      await signOut(auth);
    } catch (err) {
      console.error("[CustomerSampleScreen] logout error:", err);
      setError("Failed to log out. Please try again.");
    }
  };

  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      window.innerWidth < 1280 &&
      activeTab === "profile" &&
      !requestedView
    ) {
      setActiveTab("request");
    }
  }, [activeTab, requestedView]);

  // ---------------------------------------------------
  // UI states
  // ---------------------------------------------------
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="w-full max-w-lg">
          <SurfaceState
            tone="loading"
            title="Loading your portal"
            description="Pulling in your company, locations, catalog, and sample history."
          />
        </div>
      </div>
    );
  }

  if (!firebaseUser) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="w-full max-w-lg">
          <SurfaceState
            tone="error"
            title="Portal unavailable"
            description="No user is logged in. Please sign in again."
          />
        </div>
      </div>
    );
  }

  if (!isCustomer) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="w-full max-w-lg">
          <SurfaceState
            tone="empty"
            title="Customer portal only"
            description="This page is only available to customer accounts."
          />
        </div>
      </div>
    );
  }

  const cartCount = cart?.distinctCount || 0;
  const orderCount = Array.isArray(sampleOrders) ? sampleOrders.length : 0;
  const activeCustomerMembership = userProfile?.activeCustomerMembership || null;
  const showCompanySwitcher = customerMemberships.length > 1;

  const renderRequestTab = () => (
    accounts.length === 0 ? (
      <SurfaceState
        tone="empty"
        title="No locations available yet"
        description="Ask your distributor to add or invite one of your locations before submitting a sample request."
      />
    ) : inventory.length === 0 ? (
      <SurfaceState
        tone="empty"
        title="Catalog coming soon"
        description="There are no customer-visible sample items available for this distributor yet."
      />
    ) : (
      <CustomerSampleForm
        form={form}
        inventory={inventory}
        accounts={accounts}
        repUser={repUser}
        saving={saving}
        onSubmit={handleSubmit}
        onChange={handleFormChange}
        onClear={handleClearForm}
        deliveryDays={company?.deliveryDays || []}
        sampleRequestLeadDays={company?.sampleRequestLeadDays || 0}
        requireReasonForSample={!!company?.featureFlags?.requireReasonForSample}
        maxItemsPerOrder={company?.featureFlags?.maxItemsPerOrder ?? null}
        isVerified={isVerified}
      />
    )
  );

  const renderOrdersTab = () =>
    sampleOrders.length > 0 ? (
      <CustomerSampleOrders
        requests={sampleOrders}
        visibleAccountIds={visibleAccountIds}
        repUser={repUser}
        onSelectRequest={null}
        onSubmitFeedback={handleSubmitFeedback}
        feedbackSavingId={feedbackSavingId}
        sampleStatusLabelSettings={{
          companyType: company?.businessType || company?.companyType || "",
          businessType: company?.businessType || company?.companyType || "",
          useIndustrySampleStatusLabels: shouldUseIndustrySampleStatusLabels(company),
        }}
      />
    ) : (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center">
        <div className="text-sm font-semibold text-slate-900">No sample orders yet</div>
        <p className="mt-1 text-sm text-slate-500">
          Submit your first request and your order history will show up here.
        </p>
      </div>
    );

  const renderCatalogTab = () =>
    catalogEnabled ? (
      <CustomerCatalogWithCart
        inventory={inventory}
        cart={cart}
        onGoToRequest={() => {
          setActiveTab("request");
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        onClearOrder={handleClearForm}
      />
    ) : (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-10 text-center">
        <Lock className="mx-auto h-6 w-6 text-slate-400" />
        <div className="mt-3 text-sm font-semibold text-slate-900">Catalog unavailable</div>
        <p className="mt-1 text-sm text-slate-500">
          Your distributor has this section turned off right now.
        </p>
      </div>
    );

  const renderProfileTab = () => (
    <CustomerInfoPanel
      customerName={userProfile?.fullName || firebaseUser.displayName || "Customer"}
      customerEmail={userProfile?.email || firebaseUser.email}
      isVerified={isVerified}
      verifiedOnLabel={verifiedOnLabel}
      onSendVerification={handleSendVerification}
      verificationSending={verificationSending}
      verificationMessage={verificationMessage}
      company={company}
      repUser={repUser}
      myAccount={myAccount}
      accounts={accounts}
      primaryAccountId={primaryAccountId || userProfile?.primaryAccountId || ""}
      settingPrimary={settingPrimary}
      onSetPrimaryLocation={handleSetPrimaryLocation}
      onLogout={handleLogout}
      showAddLocation={showAddLocation}
      setShowAddLocation={setShowAddLocation}
      newLocation={newLocation}
      setNewLocation={setNewLocation}
      addLocationSaving={addLocationSaving}
      onAddLocationSubmit={handleAddLocationSubmit}
      profileForm={profileForm}
      setProfileForm={setProfileForm}
      userRole={userProfile?.role}
      isEditing={profileEditing}
      savingProfile={profileSaving}
      onEditProfile={() => setProfileEditing(true)}
      onCancelProfile={() => {
        setProfileForm({
          fullName: userProfile?.fullName || "",
          phone: userProfile?.phone || "",
        });
        setProfileEditing(false);
      }}
      onSaveProfile={handleProfileSave}
      onSaveLocation={handleSaveLocation}
      locationSavingId={locationSavingId}
      onOpenLegal={(tab) => {
        window.dispatchEvent(new CustomEvent("open-legal-modal", { detail: { tab } }));
      }}
      initialView={requestedView === "company" ? "company" : "profile"}
    />
  );

  const mobileTabs = [
    { id: "request", label: "Request", Icon: ClipboardList, badge: cartCount },
    { id: "orders", label: "Orders", Icon: Package, badge: orderCount },
    { id: "catalog", label: "Catalog", Icon: BookOpen, locked: !catalogEnabled },
  ];
  const desktopActiveTab = activeTab === "catalog" ? "catalog" : "request";

  return (
    <div className="w-full max-w-none mx-auto space-y-6 px-4 sm:px-6 lg:px-10 py-4">
      {error && (
        <SurfaceState
          tone="error"
          compact
          title="Portal issue"
          description={error}
        />
      )}

      {showCompanySwitcher ? (
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                Ordering From
              </div>
              <div className="mt-1 text-sm font-semibold text-slate-900">
                {activeCustomerMembership?.companyName || company?.businessName || company?.name || "Selected company"}
              </div>
            </div>

            <div className="sm:min-w-[260px]">
              <select
                value={companyId || ""}
                onChange={(e) => handleSwitchCompany(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {customerMemberships.map((membership) => (
                  <option key={membership.companyId} value={membership.companyId}>
                    {membership.companyName || membership.companyId}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      ) : null}

      <div className="xl:hidden">
        <div className="bg-white overflow-hidden">
          <div className="border-b border-slate-200 px-0 py-3">
            <nav className="inline-flex w-full rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
              <div className="grid w-full grid-cols-3 gap-1">
              {mobileTabs.map(({ id, label, Icon, badge, locked }) => {
                const active = activeTab === id;
                const disabled = Boolean(locked);
                return (
                  <button
                    key={id}
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      if (disabled) return;
                      setActiveTab(id);
                    }}
                    className={`flex min-w-0 items-center justify-center gap-1.5 rounded-2xl px-2 py-3 text-[11px] font-semibold transition-all ${
                      active
                        ? "bg-blue-50 text-blue-700 shadow-sm"
                        : disabled
                        ? "cursor-not-allowed text-slate-400"
                        : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    }`}
                    title={
                      id === "catalog" && !catalogEnabled
                        ? "Catalog is disabled by your distributor"
                        : label
                    }
                  >
                    <div className="flex min-w-0 items-center gap-1.5">
                      <Icon className="h-4 w-4 shrink-0" />
                      {locked ? <Lock className="h-3.5 w-3.5 text-slate-400" /> : null}
                      <span className="truncate">{label}</span>
                      {typeof badge === "number" && badge > 0 ? (
                        <span className="inline-flex min-w-[1.35rem] items-center justify-center rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] text-blue-800">
                          {badge}
                        </span>
                      ) : null}
                    </div>
                  </button>
                );
              })}
              </div>
            </nav>
          </div>

          <div className="pt-3">
            {activeTab === "request" ? renderRequestTab() : null}
            {activeTab === "orders" ? renderOrdersTab() : null}
            {activeTab === "catalog" ? renderCatalogTab() : null}
            {activeTab === "profile" ? renderProfileTab() : null}
          </div>
        </div>
      </div>

      <div className="hidden xl:grid xl:grid-cols-3 gap-6">
        {/* LEFT */}
        <div className="xl:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-visible">
            {/* Tabs */}
            <div className="border-b border-gray-200 bg-gray-50">
              <nav className="flex text-sm">
                <button
                  type="button"
                  onClick={() => setActiveTab("request")}
                  className={`flex items-center gap-2 px-5 py-3.5 font-medium transition-all relative ${
                    desktopActiveTab === "request"
                      ? "text-blue-700 bg-white"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  <List className="w-4 h-4" />
                  <span>Request</span>
                  {cartCount > 0 ? (
                    <span className="ml-1 inline-flex items-center justify-center text-[11px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
                      {cartCount}
                    </span>
                  ) : null}
                  {desktopActiveTab === "request" && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
                  )}
                </button>

                <button
                  type="button"
                  disabled={!catalogEnabled}
                  onClick={() => {
                    if (!catalogEnabled) return;
                    setActiveTab("catalog");
                  }}
                  className={`flex items-center gap-2 px-5 py-3.5 font-medium transition-all relative ${
                    desktopActiveTab === "catalog"
                      ? "text-blue-700 bg-white"
                      : !catalogEnabled
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  }`}
                  title={
                    catalogEnabled
                      ? "Browse available sample items"
                      : "Catalog is disabled by your distributor"
                  }
                >
                  <BookOpen className="w-4 h-4" />
                  <span>Catalog</span>
                  {!catalogEnabled ? <Lock className="w-3.5 h-3.5 text-gray-400" /> : null}
                  {desktopActiveTab === "catalog" && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
                  )}
                </button>
              </nav>
            </div>

            {/* Body */}
            <div className="p-4 md:p-5 overflow-visible min-h-[min(56rem,calc(100vh-10rem))]">
              {desktopActiveTab === "request" ? (
                <div className="space-y-6">
                  {renderRequestTab()}

                  {sampleOrders.length > 0 ? renderOrdersTab() : null}
                </div>
              ) : (
                renderCatalogTab()
              )}
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <div className="space-y-6 xl:pl-2">
          {renderProfileTab()}
        </div>
      </div>
    </div>
  );
};

export default CustomerSampleScreen;
